import { useQuery } from '@tanstack/react-query'
import { getMerchantById } from '../lib/api/merchantService'
import type { MerchantResponse } from '../lib/api/generated/models/merchant-response'

/**
 * Hook to fetch merchant profile data from the backend API
 *
 * @param merchantId - The merchant ID to fetch profile for
 * @returns React Query result with MerchantResponse data
 */
export const useMerchantProfile = (merchantId: string | null) => {
  return useQuery<MerchantResponse, Error>({
    queryKey: ['merchant-profile', merchantId],
    queryFn: async () => {
      if (!merchantId) {
        throw new Error('Merchant ID is required')
      }

      // Call real API endpoint: GET /api/v1/merchants/{merchantId}
      return await getMerchantById(merchantId)
    },
    enabled: !!merchantId,
    staleTime: 1000 * 60 * 5, // 5 minutes - consider data fresh for 5 min
    cacheTime: 1000 * 60 * 10, // 10 minutes - keep in cache for 10 min
    retry: (failureCount, error: any) => {
      // Don't retry on 404 - merchant not found is not a transient error
      if (error?.response?.status === 404) {
        return false
      }
      // Retry up to 3 times for other errors (network issues, 5xx, etc.)
      return failureCount < 3
    },
  })
}
